/**
 * Server-provided responsive-image spec plumbing for the model-finder detail
 * tile (`img.datte-mf-page-detail-image`) — the JS consumer of the
 * `datte-mf-page-detail-image` COMPONENT_REGIONS row.
 *
 * Mirrors the product-attribute-description helpers (ADR-0015): the PHP SSoT
 * renders the spec into the MF SSR roots as `data-rimg-spec` (via
 * `{responsiveImageSpec}`), the JS reads it off the nearest ancestor at
 * runtime, and MF_TILE_FALLBACK keeps the tile responsive when the attribute
 * is absent/malformed. One fallback const per consumer module, each with its
 * own drift gate — no shared JS region mirror (§8 D-8.3).
 *
 * @see src/Services/ResponsiveImage/ResponsiveImageConfig.php — `datte-mf-page-detail-image` row
 * @see src/ersatzteile/model-finder-page.tpl / model-finder-ssr.tpl — emit data-rimg-spec
 * @see docs/plans/2026-07-09 - interactive-shop-foundation/research/8-responsive-integration/8-responsive-integration.md §3.3
 */
import type { ResponsiveImageSpec } from '../responsive-image';

/**
 * Last-resort spec for the MF detail tile, used ONLY when no `data-rimg-spec`
 * ancestor is found. Kept BYTE-IDENTICAL to the `datte-mf-page-detail-image`
 * COMPONENT_REGIONS row in `ResponsiveImageConfig.php`, pinned by the regex
 * drift gate in `tests/unit/responsive-image/region-rows-fusion.test.ts`.
 */
export const MF_TILE_FALLBACK: ResponsiveImageSpec = {
  mode: 'remote',
  maxWidth: 220,
  sizes:
    '(min-width:769px) 200px, (min-width:673px) 150px, (min-width:551px) 220px, (min-width:376px) 40vw, 150px',
};

/**
 * Read the tile's responsive spec from the nearest ancestor carrying a
 * `data-rimg-spec` attribute (the PHP-provided SSoT on the MF SSR root).
 * Falls back to {@link MF_TILE_FALLBACK} when absent or malformed.
 */
export function readMfTileSpec(el: Element): ResponsiveImageSpec {
  const raw = el.closest('[data-rimg-spec]')?.getAttribute('data-rimg-spec');
  if (!raw) {
    return MF_TILE_FALLBACK;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ResponsiveImageSpec> | null;
    // An unknown region yields all-null keys (regionByClass() === []); treat an
    // absent mode as "no usable spec" and fall back.
    if (parsed && typeof parsed.mode === 'string') {
      return parsed as ResponsiveImageSpec;
    }
  } catch {
    // malformed JSON → fall through to the fallback
  }
  return MF_TILE_FALLBACK;
}

/**
 * Read the CF transform origin from the nearest `data-rimg-origin` ancestor —
 * the global `<body>` stamp (IplOriginGlobalFilter), so tiles are sized against
 * the SAME origin the PHP strategy uses (on a staging clone that is the prod CF
 * zone, not the staging host). `undefined` → transform() defaults to
 * `window.location.origin`.
 */
export function readMfTileOrigin(el: Element): string | undefined {
  const raw = el.closest('[data-rimg-origin]')?.getAttribute('data-rimg-origin')?.trim();
  return raw ? raw : undefined;
}

/**
 * D-8.1 guard: only the composed tier resolves to an ABSOLUTE https URL (R2
 * public host); the demoted `tkategoriepict` bridge tier stays a relative
 * `/media/image/category/...` path. A CF transform of a relative path would
 * fetch PROD media on a staging clone, and the region's square-source `sizes`
 * basis only holds for the composed 1200×1200 tiles — so relative sources are
 * never transformed.
 */
export function isTransformableTileSrc(src: string): boolean {
  return /^https?:\/\//i.test(src);
}
